export const DEFAULT_THEME_COLOR = "#3f0a35";
export const THEME_CACHE_KEY = "ac-portal-theme";

export type PortalTheme = {
  id?: number;
  name?: string;
  color?: string;
  date_start?: string;
  date_end?: string;
  is_current?: boolean;
};

type Rgb = { r: number; g: number; b: number };
type Hsl = { h: number; s: number; l: number };

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const normalizeThemeColor = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  const short = /^#([0-9a-f]{3})$/i.exec(normalized);
  if (short) {
    return `#${short[1]
      .split("")
      .map((part) => part + part)
      .join("")}`;
  }
  return /^#[0-9a-f]{6}$/i.test(normalized) ? normalized : null;
};

const hexToRgb = (hex: string): Rgb => ({
  r: parseInt(hex.slice(1, 3), 16),
  g: parseInt(hex.slice(3, 5), 16),
  b: parseInt(hex.slice(5, 7), 16),
});

const rgbToHex = ({ r, g, b }: Rgb) =>
  `#${[r, g, b]
    .map((part) => Math.round(clamp(part, 0, 255)).toString(16).padStart(2, "0"))
    .join("")}`;

const rgbToHsl = ({ r, g, b }: Rgb): Hsl => {
  const [red, green, blue] = [r, g, b].map((part) => part / 255);
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;
  if (delta) {
    if (max === red) hue = ((green - blue) / delta) % 6;
    else if (max === green) hue = (blue - red) / delta + 2;
    else hue = (red - green) / delta + 4;
  }
  hue = Math.round(hue * 60);
  if (hue < 0) hue += 360;
  const lightness = (max + min) / 2;
  const saturation = delta ? delta / (1 - Math.abs(2 * lightness - 1)) : 0;
  return { h: hue, s: saturation * 100, l: lightness * 100 };
};

const hslToRgb = ({ h, s, l }: Hsl): Rgb => {
  const saturation = clamp(s, 0, 100) / 100;
  const lightness = clamp(l, 0, 100) / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const segment = ((h % 360) + 360) % 360 / 60;
  const x = chroma * (1 - Math.abs((segment % 2) - 1));
  const [red, green, blue] =
    segment < 1 ? [chroma, x, 0] :
    segment < 2 ? [x, chroma, 0] :
    segment < 3 ? [0, chroma, x] :
    segment < 4 ? [0, x, chroma] :
    segment < 5 ? [x, 0, chroma] : [chroma, 0, x];
  const match = lightness - chroma / 2;
  return { r: (red + match) * 255, g: (green + match) * 255, b: (blue + match) * 255 };
};

const tone = (hsl: Hsl, saturation: number, lightness: number) =>
  rgbToHex(hslToRgb({ h: hsl.h, s: saturation, l: lightness }));

const mixWithWhite = (rgb: Rgb, amount: number): Rgb => ({
  r: rgb.r * amount + 255 * (1 - amount),
  g: rgb.g * amount + 255 * (1 - amount),
  b: rgb.b * amount + 255 * (1 - amount),
});

const contrastColor = ({ r, g, b }: Rgb) => {
  const channel = (value: number) => {
    const normalized = value / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : Math.pow((normalized + 0.055) / 1.055, 2.4);
  };
  const luminance = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
  return luminance > 0.42 ? "#171217" : "#ffffff";
};

export const createThemePalette = (requestedColor: unknown) => {
  const primary = normalizeThemeColor(requestedColor) || DEFAULT_THEME_COLOR;
  if (primary === DEFAULT_THEME_COLOR) {
    return {
      primary,
      accent: "#760260",
      soft: "#592e50",
      highlight: "#8d3f7d",
      deep: "#8c2464",
      shade: "#300528",
      ink: "#302433",
      text: "#514655",
      textMuted: "#746579",
      accentMuted: "#a55c95",
      borderStrong: "#d9c8dd",
      border: "#e7dce9",
      surface: "#f5f2f4",
      tintStrong: "#f4eff6",
      tint: "#f8f0f7",
      canvas: "#faf8fb",
      canvasAlt: "#faf7fc",
      onPrimary: "#ffffff",
    };
  }
  const rgb = hexToRgb(primary);
  const hsl = rgbToHsl(rgb);
  return {
    primary,
    accent: tone(hsl, clamp(hsl.s * 0.98, 48, 92), clamp(hsl.l + 13, 28, 48)),
    soft: tone(hsl, clamp(hsl.s * 0.52, 28, 58), clamp(hsl.l + 17, 30, 55)),
    highlight: tone(hsl, clamp(hsl.s * 0.62, 35, 68), clamp(hsl.l + 29, 42, 67)),
    deep: tone(hsl, clamp(hsl.s * 0.88, 45, 85), clamp(hsl.l + 5, 18, 38)),
    shade: tone(hsl, clamp(hsl.s * 0.9, 50, 88), clamp(hsl.l - 4, 7, 25)),
    ink: tone(hsl, 14, 17),
    text: tone(hsl, 11, 30),
    textMuted: tone(hsl, 10, 44),
    accentMuted: tone(hsl, clamp(hsl.s * 0.42, 25, 55), 50),
    borderStrong: tone(hsl, 24, 83),
    border: tone(hsl, 22, 89),
    surface: rgbToHex(mixWithWhite(rgb, 0.06)),
    tintStrong: tone(hsl, 28, 95),
    tint: tone(hsl, 36, 96),
    canvas: tone(hsl, 20, 98),
    canvasAlt: tone(hsl, 30, 98),
    onPrimary: contrastColor(rgb),
  };
};

export const extractPortalTheme = (payload: unknown): PortalTheme | null => {
  let candidate: any = payload;
  for (let depth = 0; depth < 3; depth += 1) {
    if (Array.isArray(candidate)) candidate = candidate[0];
    if (!candidate || typeof candidate !== "object") return null;
    if ("color" in candidate || "is_current" in candidate) return candidate as PortalTheme;
    candidate = candidate.result ?? candidate.data;
  }
  return null;
};

const rgbChannels = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  return `${Math.round(r)} ${Math.round(g)} ${Math.round(b)}`;
};

export const applyThemeColor = (color: unknown, root = document.documentElement) => {
  const palette = createThemePalette(color);
  const colors = {
    primary: palette.primary,
    accent: palette.accent,
    soft: palette.soft,
    highlight: palette.highlight,
    deep: palette.deep,
    shade: palette.shade,
    ink: palette.ink,
    text: palette.text,
    "text-muted": palette.textMuted,
    "accent-muted": palette.accentMuted,
    "border-strong": palette.borderStrong,
    border: palette.border,
    surface: palette.surface,
    "tint-strong": palette.tintStrong,
    tint: palette.tint,
    canvas: palette.canvas,
    "canvas-alt": palette.canvasAlt,
  };
  Object.entries(colors).forEach(([name, value]) => {
    root.style.setProperty(`--ac-theme-${name}`, value);
    root.style.setProperty(`--ac-theme-${name}-rgb`, rgbChannels(value));
  });
  root.style.setProperty("--ac-theme-on-primary", palette.onPrimary);
  root.style.colorScheme = "light";
  return palette;
};

export const themeIsActive = (theme: PortalTheme | null) =>
  Boolean(theme && theme.is_current !== false && normalizeThemeColor(theme.color));
